/**
 * Search Components Tool
 *
 * Allows AI models to search for components by name, category, or functionality.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import componentCatalog from '../data/component-catalog.json' with { type: 'json' };
import type { ComponentCatalog } from '../types.js';

const catalog = componentCatalog as ComponentCatalog;

const SearchComponentsSchema = z.object({
  query: z.string().describe('Search query (component name, description, or functionality)'),
  category: z
    .enum([
      'core',
      'form',
      'layout',
      'feedback',
      'data',
      'overlay',
      'navigation',
      'branding',
      'chart',
    ])
    .optional()
    .describe('Filter by component category'),
  limit: z.number().min(1).max(50).default(10).describe('Maximum number of results'),
});

/**
 * Register the search_components tool
 */
export function registerSearchComponentsTool(server: McpServer): void {
  server.tool(
    'search_components',
    'Search for components by name, category, or functionality',
    SearchComponentsSchema.shape,
    async (params) => {
      const { query, category, limit } = SearchComponentsSchema.parse(params);
      const queryLower = query.toLowerCase().trim();

      // Check for wildcard or "list all" queries
      const isListAll =
        queryLower === '*' ||
        queryLower === 'all' ||
        queryLower === '' ||
        queryLower === 'list' ||
        queryLower === 'list all';

      // Split query into words for multi-word search (supports "form input" matching components with both words)
      const queryWords = queryLower.split(/\s+/).filter((word) => word.length > 0);

      const results = catalog.components
        .filter((component) => {
          // Category filter
          if (category && component.category !== category) {
            return false;
          }

          // If listing all, include everything (respecting category filter)
          if (isListAll) {
            return true;
          }

          // Search in name, description, props, and related components
          const searchableText = [
            component.name,
            component.description,
            ...component.props.map((p) => p.name),
            ...component.props.map((p) => p.description),
            ...component.relatedComponents,
            component.category,
          ]
            .join(' ')
            .toLowerCase();

          // For multi-word queries, check if ANY word matches (OR logic)
          // This allows "form input" to find Input (form category) or FormField
          if (queryWords.length > 1) {
            return queryWords.some((word) => searchableText.includes(word));
          }

          // Single word: exact substring match
          return searchableText.includes(queryLower);
        })
        // Sort by relevance: exact name match first, then partial name match, then others
        .sort((a, b) => {
          const aNameLower = a.name.toLowerCase();
          const bNameLower = b.name.toLowerCase();

          // Exact name match gets highest priority
          const aExactMatch = queryWords.some((word) => aNameLower === word);
          const bExactMatch = queryWords.some((word) => bNameLower === word);
          if (aExactMatch && !bExactMatch) return -1;
          if (bExactMatch && !aExactMatch) return 1;

          // Name starts with query word gets second priority
          const aStartsWith = queryWords.some((word) => aNameLower.startsWith(word));
          const bStartsWith = queryWords.some((word) => bNameLower.startsWith(word));
          if (aStartsWith && !bStartsWith) return -1;
          if (bStartsWith && !aStartsWith) return 1;

          // Name contains query word gets third priority
          const aContains = queryWords.some((word) => aNameLower.includes(word));
          const bContains = queryWords.some((word) => bNameLower.includes(word));
          if (aContains && !bContains) return -1;
          if (bContains && !aContains) return 1;

          // Alphabetical as fallback
          return aNameLower.localeCompare(bNameLower);
        })
        .slice(0, limit)
        .map((c) => ({
          name: c.name,
          category: c.category,
          description: c.description,
          importPath: c.importPath,
          propsCount: c.props.length,
          variants: c.variants.map((v) => v.name),
        }));

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify(
              {
                query,
                category: category || 'all',
                resultCount: results.length,
                results,
                hint:
                  results.length === 0
                    ? 'Try broader search terms or remove the category filter'
                    : results.length === limit
                      ? `Showing first ${limit} results. Refine your search for more specific results.`
                      : undefined,
              },
              null,
              2
            ),
          },
        ],
      };
    }
  );
}
